import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress, ThemeProvider } from "@mui/material";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import DialogChatAPI from "../../../api/dialogChatApi";
import SessionsApi from "../../../api/sessionApi";
import StandartButton from "../../../components/StandartButton/StandartButton";
import { TextChatBodyRenderer } from "../../../components/TextChat/TextChatMessage/TextChatMessage.types";
import strings from "../../../strings";
import { RealTimeBooking } from "../../../types/TypeSession";
import { TypeUserData } from "../../../types/TypeUsers";
import { getTranslation } from "../../../utils/getTranslation";
import { theme } from "../../../utils/theme";
import s from "../GlobalChat.module.css";
import { GlobalChatMessage } from "./globalChatCache";
import {
  canJoinRealTimeBooking,
  getBookingCancelReason,
  getBookingRemainingTime,
  getBookingWorkflowState,
  GLOBAL_CHAT_BOOKING_POLL_INTERVAL_MS,
  shouldPollRealTimeBooking,
} from "./globalChatBookingWorkflow";

type BookingResource = {
  booking: RealTimeBooking;
  isLoading?: boolean;
  error?: string;
  localCancelReason?: string;
};

type UseGlobalChatBookingResourcesParams = {
  token: string | null;
  currentUser?: TypeUserData | null;
  setUserData?: Dispatch<SetStateAction<TypeUserData | null | undefined>>;
  messages: GlobalChatMessage[];
  onBookingMessageRefresh?: (message: GlobalChatMessage) => void;
};

const mergeBookingIntoMessage = (
  message: GlobalChatMessage,
  booking: RealTimeBooking,
): GlobalChatMessage => ({
  ...message,
  realTimeBooking: booking,
});

export const useGlobalChatBookingResources = ({
  token,
  currentUser,
  setUserData,
  messages,
  onBookingMessageRefresh,
}: UseGlobalChatBookingResourcesParams) => {
  const navigate = useNavigate();
  const [resourcesByMessageId, setResourcesByMessageId] = useState<
    Record<string, BookingResource>
  >({});
  const [tick, setTick] = useState(0);
  const bookingMessages = useMemo(
    () => messages.filter((message) => message.realTimeBooking),
    [messages],
  );

  useEffect(() => {
    setResourcesByMessageId((prev) => {
      const next = { ...prev };
      let didChange = false;

      bookingMessages.forEach((message) => {
        if (!message.realTimeBooking) {
          return;
        }

        if (next[message.messageId]?.booking) {
          return;
        }

        didChange = true;
        next[message.messageId] = {
          ...next[message.messageId],
          booking: message.realTimeBooking,
        };
      });

      Object.keys(next).forEach((messageId) => {
        if (!bookingMessages.some((message) => message.messageId === messageId)) {
          didChange = true;
          delete next[messageId];
        }
      });

      return didChange ? next : prev;
    });
  }, [bookingMessages]);

  useEffect(() => {
    const hasPendingBooking = bookingMessages.some((message) =>
      shouldPollRealTimeBooking(
        resourcesByMessageId[message.messageId]?.booking ||
          message.realTimeBooking,
      ),
    );

    if (!hasPendingBooking) {
      return;
    }

    const timerId = window.setInterval(() => {
      setTick((prev) => prev + 1);
    }, 1000);

    return () => window.clearInterval(timerId);
  }, [bookingMessages, resourcesByMessageId]);

  useEffect(() => {
    if (!token || bookingMessages.length === 0) {
      return;
    }

    const pollableMessages = bookingMessages.filter((message) =>
      shouldPollRealTimeBooking(
        resourcesByMessageId[message.messageId]?.booking ||
          message.realTimeBooking,
      ),
    );

    if (!pollableMessages.length) {
      return;
    }

    const refreshMessages = () => {
      pollableMessages.forEach((message) => {
        DialogChatAPI.getDialogMessageById(token, message.messageId)
          .then((response) => {
            const booking = response.responseMessage?.realTimeBooking;
            if (!response.status || !booking) {
              return;
            }

            setResourcesByMessageId((prev) => ({
              ...prev,
              [message.messageId]: {
                ...prev[message.messageId],
                booking,
                error: "",
              },
            }));
            onBookingMessageRefresh?.(mergeBookingIntoMessage(message, booking));
          })
          .catch(() => {
            setResourcesByMessageId((prev) => ({
              ...prev,
              [message.messageId]: {
                ...prev[message.messageId],
                booking:
                  prev[message.messageId]?.booking ||
                  message.realTimeBooking!,
                error: strings.failedStatus || "Failed",
              },
            }));
          });
      });
    };

    const intervalId = window.setInterval(
      refreshMessages,
      GLOBAL_CHAT_BOOKING_POLL_INTERVAL_MS,
    );

    return () => window.clearInterval(intervalId);
  }, [bookingMessages, onBookingMessageRefresh, resourcesByMessageId, token]);

  const setResourceState = useCallback(
    (messageId: string, changes: Partial<BookingResource>) => {
      setResourcesByMessageId((prev) => ({
        ...prev,
        [messageId]: {
          ...prev[messageId],
          ...changes,
          booking: changes.booking || prev[messageId]?.booking,
        } as BookingResource,
      }));
    },
    [],
  );

  const acceptBooking = useCallback(
    async (message: GlobalChatMessage, booking: RealTimeBooking) => {
      if (!token || !currentUser?._id) {
        return;
      }

      setResourceState(message.messageId, { isLoading: true, error: "" });

      try {
        const response = await SessionsApi.acceptRealTimeSessionRequest(
          token,
          currentUser._id,
          booking._id,
        );

        if (!response.status) {
          setResourceState(message.messageId, {
            isLoading: false,
            error: response.message || strings.failedStatus || "Failed",
          });
          return;
        }

        const nextBooking = {
          ...booking,
          isAccepted: true,
          bookedSession: response.session?._id || booking.bookedSession,
        };

        setResourceState(message.messageId, {
          booking: nextBooking,
          isLoading: false,
          error: "",
        });
        onBookingMessageRefresh?.(mergeBookingIntoMessage(message, nextBooking));
        setUserData?.((prev) =>
          prev
            ? {
                ...prev,
                specialistIds: prev.specialistIds?.map((item) => ({
                  ...item,
                  receiveRealTimeOffers: false,
                })),
              }
            : prev,
        );
      } catch {
        setResourceState(message.messageId, {
          isLoading: false,
          error: strings.failedStatus || "Failed",
        });
      }
    },
    [currentUser?._id, onBookingMessageRefresh, setResourceState, setUserData, token],
  );

  const rejectBooking = useCallback(
    async (
      message: GlobalChatMessage,
      booking: RealTimeBooking,
      reason: string,
    ) => {
      if (!token || !currentUser?._id || !reason.trim()) {
        return;
      }

      setResourceState(message.messageId, { isLoading: true, error: "" });

      try {
        const response = await SessionsApi.cancelRealTimeSessionRequest(
          token,
          currentUser._id,
          booking._id,
          reason.trim(),
        );

        if (!response.status) {
          setResourceState(message.messageId, {
            isLoading: false,
            error: response.message || strings.failedStatus || "Failed",
          });
          return;
        }

        const nextBooking = {
          ...booking,
          isExpired: true,
          cancelReason: reason.trim(),
        };

        setResourceState(message.messageId, {
          booking: nextBooking,
          isLoading: false,
          error: "",
          localCancelReason: reason.trim(),
        });
        onBookingMessageRefresh?.(mergeBookingIntoMessage(message, nextBooking));
      } catch {
        setResourceState(message.messageId, {
          isLoading: false,
          error: strings.failedStatus || "Failed",
        });
      }
    },
    [currentUser?._id, onBookingMessageRefresh, setResourceState, token],
  );

  const joinBooking = useCallback(
    (booking: RealTimeBooking) => {
      if (!booking.bookedSession) {
        return;
      }

      navigate("/session-meet", {
        state: {
          sessionId: booking.bookedSession,
        },
      });
    },
    [navigate],
  );

  const getResource = useCallback(
    (message: GlobalChatMessage) =>
      resourcesByMessageId[message.messageId] ||
      (message.realTimeBooking
        ? { booking: message.realTimeBooking }
        : undefined),
    [resourcesByMessageId],
  );

  const renderBookingMessage = useCallback<TextChatBodyRenderer>(
    ({ message, name, surname }) => {
      const sourceMessage = messages.find((item) => item.messageId === message.id);
      const resource = sourceMessage ? getResource(sourceMessage) : undefined;
      const booking = resource?.booking || message.realTimeBooking;

      return (
        <BookingMessageBody
          acceptBooking={acceptBooking}
          booking={booking}
          currentUser={currentUser}
          joinBooking={joinBooking}
          name={name}
          rejectBooking={rejectBooking}
          resource={resource}
          sourceMessage={sourceMessage}
          surname={surname}
          senderKind={message.senderKind}
        />
      );
    },
    [
      acceptBooking,
      currentUser,
      getResource,
      joinBooking,
      messages,
      rejectBooking,
      tick,
    ],
  );

  return {
    renderers: {
      booking: renderBookingMessage,
    },
  };
};

const BookingMessageBody = ({
  acceptBooking,
  booking,
  currentUser,
  joinBooking,
  name,
  rejectBooking,
  resource,
  sourceMessage,
  surname,
  senderKind,
}: {
  acceptBooking: (
    message: GlobalChatMessage,
    booking: RealTimeBooking,
  ) => Promise<void>;
  booking?: RealTimeBooking;
  currentUser?: TypeUserData | null;
  joinBooking: (booking: RealTimeBooking) => void;
  name?: string;
  rejectBooking: (
    message: GlobalChatMessage,
    booking: RealTimeBooking,
    reason: string,
  ) => Promise<void>;
  resource?: BookingResource;
  sourceMessage?: GlobalChatMessage;
  surname?: string;
  senderKind: string;
}) => {
  const state = getBookingWorkflowState(booking);
  const remainingTime = getBookingRemainingTime(booking);
  const isCurrentUserRequest = senderKind === "currentUser";
  const canJoin = canJoinRealTimeBooking(booking);
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const hasAutoRejectedRef = useRef(false);

  useEffect(() => {
    if (
      !sourceMessage ||
      !booking ||
      resource?.isLoading ||
      hasAutoRejectedRef.current ||
      state !== "pending" ||
      remainingTime.totalMs > 0
    ) {
      return;
    }

    hasAutoRejectedRef.current = true;
    void rejectBooking(sourceMessage, booking, strings.autoCancelSessionReason);
  }, [
    booking,
    remainingTime.totalMs,
    rejectBooking,
    resource?.isLoading,
    sourceMessage,
    state,
  ]);

  if (!booking || !sourceMessage) {
    return (
      <div className={s.bookingMessageBody}>
        <p className={s.bookingText}>{strings.sessionReques}</p>
      </div>
    );
  }

  const cancelReason =
    resource?.localCancelReason ||
    getBookingCancelReason(booking, currentUser?.selectedLanguage);

  return (
    <div className={s.bookingMessageBody}>
          {!isCurrentUserRequest ? (
            <>
              <p className={s.bookingTitle}>
                {strings.receivedRequesrSession}{" "}
                <span>{`${name || ""} ${surname || ""}`.trim()}</span>
              </p>
              {booking.tags?.length ? (
                <div className={s.bookingDetails}>
                  {booking.tags.map((tag, index) => (
                    <div className={s.bookingDetailRow} key={`${tag._id}-${index}`}>
                      <span>
                        {
                          [
                            strings.direction,
                            strings.topic,
                            strings.result,
                          ][tag.level - 1]
                        }
                        :
                      </span>
                      <span>
                        {getTranslation(
                          tag.labels,
                          currentUser?.selectedLanguage,
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}
              {booking.subject && (
                <div className={s.bookingDetailRow}>
                  <span>{strings.infoModalComment}</span>
                  <span>{booking.subject}</span>
                </div>
              )}
              {booking.project && (
                <div className={s.bookingDetailRow}>
                  <span>{strings.projectRealTimeSession}</span>
                  <span>
                    {getTranslation(
                      booking.project.title,
                      currentUser?.selectedLanguage,
                    )}
                  </span>
                </div>
              )}
              {resource?.isLoading ? (
                <div className={s.bookingLoader}>
                  <ThemeProvider theme={theme}>
                    <CircularProgress size={30} color="primary" />
                  </ThemeProvider>
                </div>
              ) : state === "pending" ? (
                isRejecting ? (
                  <div className={s.bookingRejectRow}>
                    <span className={s.bookingReasonIcon}>!</span>
                    <input
                      type="text"
                      value={rejectReason}
                      placeholder={strings.reasonPlaceHodler}
                      onChange={(event) => setRejectReason(event.target.value)}
                    />
                    {rejectReason.trim() ? (
                      <button
                        type="button"
                        aria-label={strings.rejectBtn}
                        onClick={() =>
                          rejectBooking(sourceMessage, booking, rejectReason)
                        }
                      >
                        <ArrowForwardIcon fontSize="small" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        aria-label="Cancel"
                        onClick={() => setIsRejecting(false)}
                      >
                        <CloseIcon fontSize="small" />
                      </button>
                    )}
                  </div>
                ) : (
                  <div className={s.bookingActionRow}>
                    <button
                      type="button"
                      className={s.bookingAcceptButton}
                      onClick={() => acceptBooking(sourceMessage, booking)}
                    >
                      {strings.acceptBtn}
                    </button>
                    <button
                      type="button"
                      className={s.bookingRejectButton}
                      onClick={() => setIsRejecting(true)}
                    >
                      {strings.rejectBtn}
                    </button>
                    {remainingTime.totalMs > 0 && (
                      <div className={s.bookingTimer}>
                        <span>
                          {remainingTime.minutes.toString().padStart(2, "0")}
                        </span>
                        :
                        <span>
                          {remainingTime.seconds.toString().padStart(2, "0")}
                        </span>
                      </div>
                    )}
                  </div>
                )
              ) : state === "expired" ? (
                <div className={s.bookingStatusBlock}>
                  <span className={s.bookingCanceled}>
                    {strings.canceledSession}
                  </span>
                  {cancelReason && (
                    <span>
                      {strings.reasonTitle} {cancelReason}
                    </span>
                  )}
                </div>
              ) : (
                <div className={s.bookingJoinBlock}>
                  <StandartButton
                    buttonTitle={strings.joinBtn}
                    action={() => joinBooking(booking)}
                    disabled={!canJoin}
                    width="200px"
                  />
                </div>
              )}
            </>
          ) : state === "accepted" ? (
            <div className={s.bookingStatusBlock}>
              <span className={s.bookingTitle}>
                {strings.specialistConfirmedRequest}
              </span>
              <div className={s.bookingJoinBlock}>
                <StandartButton
                  buttonTitle={strings.joinBtn}
                  action={() => joinBooking(booking)}
                  disabled={!canJoin}
                  width="200px"
                />
              </div>
            </div>
          ) : state === "pending" ? (
            <div className={s.bookingStatusBlock}>
              <div className={s.bookingClientHeader}>
                <span>{strings.sessionRequest}</span>
                {remainingTime.totalMs > 0 && (
                  <span>
                    {remainingTime.minutes.toString().padStart(2, "0")} :{" "}
                    {remainingTime.seconds.toString().padStart(2, "0")}
                  </span>
                )}
              </div>
              <span>{strings.waitSpecResponse}</span>
            </div>
          ) : (
            <div className={s.bookingStatusBlock}>
              <span className={s.bookingTitle}>{strings.sessionReques}</span>
              <span className={s.bookingCanceled}>{strings.canceledSession}</span>
              {cancelReason && (
                <span>
                  {strings.reasonTitle} {cancelReason}
                </span>
              )}
            </div>
          )}
          {resource?.error && (
            <div className={s.bookingError} role="alert">
              {resource.error}
            </div>
          )}
    </div>
  );
};
